import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiClient from "../../services/apiService";
import { getErrorMessage } from "../../utils/getErrorMessage";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Email validation helper
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!email.trim()) {
      setError("Email is required");
      setLoading(false);
      return;
    }

    if (!isValidEmail(email)) {
      setError("Please enter a valid email");
      setLoading(false);
      return;
    }

    if (!password) {
      setError("Password is required");
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.post("/admin/login", {
        email,
        password,
      });

      if (response.data.type === "success") {
        toast.success(response.data.message);
        sessionStorage.setItem("2faEmail", email);
        navigate("/verify-2fa");
      }
    } catch (err) {
      setError(getErrorMessage(err, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5] shadow-sm p-4">
      {/* --- LOGIN CARD --- */}
      <div className="w-full max-w-[440px] bg-white rounded-[24px] shadow-lg p-10 flex flex-col items-center">
        {/* --- LOGO --- */}
        <div className="mb-6 flex flex-col items-center">
          <img
            src="/images/logos/logo.png"
            alt="Trulixx Fashion Logo"
            className="h-16 w-auto object-contain"
          />
        </div>

        {/* --- HEADER --- */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-slate-900 mb-1">
            Admin Dashboard
          </h1>
          <p className="text-base text-[#7D828D] font-medium">
            Sign in to access the admin dashboard
          </p>
        </div>

        {/* --- FORM --- */}
        <form className="w-full space-y-5" onSubmit={handleLogin}>
          {error && (
            <div className="w-full bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email"
              className="w-full px-4 py-3 bg-white border border-[#DFDFDF] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#6B7143] transition-all"
            />
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-4 py-3 bg-white border border-[#DFDFDF] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#6B7143] transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate("/reset-password")}
              className="text-sm font-medium text-[#556B2F] hover:underline transition-all"
            >
              Forgot your password?
            </button>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D9CBBF] hover:bg-[#c9b9ad] disabled:bg-gray-300 text-black font-medium py-3 rounded-xl text-sm transition-colors mt-2 active:scale-95 active:shadow-sm duration-100"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
